import { useState } from "react";
import { PALETTE as P } from "../tokens";

// Small ⓘ affordance + bottom sheet explaining what goes into a single
// domain score (Mind / Body / Behavior). Sibling of SignalInfoButton,
// which covers the overall Signal score; this one is per-domain and
// selects its copy from a static map keyed by domain.
//
// Copy is intentionally plain-language and static — no composition data
// is fetched, no weights are surfaced. Stays inside Haiven's
// non-clinical guardrails (conditions, not outcomes; no diagnosis).

const SHEET_STYLES = `
  @keyframes domInfoFade { from { opacity: 0; } to { opacity: 1; } }
  @keyframes domInfoRise { from { transform: translateY(24px); opacity: 0; }
                           to   { transform: translateY(0);    opacity: 1; } }
  .dom-info-trigger {
    display: inline-flex; align-items: center; justify-content: center;
    background: none; border: none; cursor: pointer; padding: 4px;
    margin: -4px 0 -4px 2px; line-height: 0;
    color: ${P.primaryMid};
    transition: color 0.15s;
  }
  .dom-info-trigger:hover { color: ${P.primary}; }
  .dom-info-backdrop {
    position: fixed; inset: 0; z-index: 1200;
    background: rgba(28,24,20,0.35);
    animation: domInfoFade 0.2s ease;
    display: flex; align-items: flex-end; justify-content: center;
  }
  .dom-info-sheet {
    width: 100%; max-width: 420px;
    max-height: 82vh; overflow-y: auto;
    background: ${P.surface};
    border-radius: 22px 22px 0 0;
    padding: 26px 24px calc(24px + env(safe-area-inset-bottom));
    box-shadow: 0 -8px 40px rgba(28,24,20,0.18);
    animation: domInfoRise 0.25s ease;
    font-family: 'DM Sans', sans-serif;
  }
`;

// Keys match DOMAIN_META in utils/domainLabels.js: psych | bio | beh.
const DOMAIN_COPY = {
  psych: {
    display: "Mind",
    body:
      "This is 100% what you tell us. No outside data, just your own check-ins.",
  },
  bio: {
    display: "Body",
    body:
      "This blends what you tell us with signals from your wearable, like sleep and recovery. If your wearable is picking up something you don't feel yet (or vice versa), this number can look different from your rating.",
  },
  beh: {
    display: "Behavior",
    body:
      "This blends what you tell us with how consistently you're completing your protocols. If you're feeling good about your habits but haven't logged completions recently, this number reflects that gap.",
  },
};

function InfoGlyph({ size = 13 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
      <circle cx="12" cy="12" r="9.2" />
      <path d="M12 11v5.2" />
      <circle cx="12" cy="7.8" r="0.4" fill="currentColor" />
    </svg>
  );
}

export default function DomainInfoButton({ domain, style }) {
  const [open, setOpen] = useState(false);
  const copy = DOMAIN_COPY[domain];

  // Unknown domain key → render nothing rather than an empty sheet.
  if (!copy) return null;

  const title = `About your ${copy.display} score`;

  return (
    <>
      <style>{SHEET_STYLES}</style>
      <button
        type="button"
        className="dom-info-trigger"
        style={style}
        aria-label={title}
        onClick={() => setOpen(true)}
      >
        <InfoGlyph />
      </button>

      {open && (
        <div
          className="dom-info-backdrop"
          onClick={() => setOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-label={title}
        >
          <div className="dom-info-sheet" onClick={(e) => e.stopPropagation()}>
            <div style={{
              fontSize: 10, letterSpacing: "0.18em", textTransform: "uppercase",
              color: P.primaryMid, fontWeight: 500, marginBottom: 12,
            }}>
              {title}
            </div>
            <p style={{
              fontSize: 13.5, color: P.text, fontWeight: 300,
              lineHeight: 1.7, margin: "0 0 18px",
            }}>
              {copy.body}
            </p>
            <button
              type="button"
              onClick={() => setOpen(false)}
              style={{
                width: "100%", padding: 15, borderRadius: 14,
                background: P.primary, color: P.bg, border: "none",
                cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
                fontSize: 13.5, fontWeight: 500,
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </>
  );
}
